
import { type Driver, type DriveStep } from "driver.js";
import { soundBoard } from "../store";
import { LibraryState, libraryState } from "../library/Library";

function toVideoElem(url: string): string {
    return `<video src='/public${url}' autoplay loop width='250px' height='auto' />`
}

export type TutorialStep = {
    driverStep: DriveStep
    onComponentMounted?: (node: HTMLElement, driver: Driver) => void;
    onDetailsOpen?: (node: HTMLElement, driver: Driver) => void;
    onSearchChanged?: (driver: Driver) => void;
}
export const WELCOME: TutorialStep = {
    driverStep: {
        popover: {
            title: 'Welcome to Bardic Tools!',
            description: "You're going to build an epic, immersive soundscape in less than 5 minutes.",
            onPopoverRender: () => {
                libraryState.set(LibraryState.Opening)
            }
        }
    }
}
export const ASK_TO_OPEN_LIBRARY: TutorialStep = {
    driverStep: {
        element: '#library-button',
        popover: {
            title: "Let's start with a template", description: 'Click here to open <b>The Library</b>.',
            showButtons: ["close"],
        }
    },
    onComponentMounted: (node: HTMLElement, driver: Driver) => {
        if (node.id == "library") {
            driver.moveNext();
        }
    }
}

export const EXPLAIN_LIBRARY: TutorialStep = {
    driverStep: {
        element: '#library',
        popover: {
            title: 'Well done!',
            description: "This is <b>The Library</b>. It has everything you need to get you started.",
            align: "center", side: "top",
        }
    },
}

export const ASK_TO_CLICK_ON_SCENES_FILTER: TutorialStep = {
    // name: "click-on-scenes-filter",
    driverStep: {
        element: '#quick-filter-Scenes',
        popover: {
            title: "Let's get started!", description: "<p>I promised epic sound in 5 minutes, so I don't have time to lose.</p><p>Click here.</p>",
            showButtons: ["close"],
        }
    },
    onComponentMounted: (node: HTMLElement, driver: Driver) => {
        if (node.classList.contains("scene-container")) {
            driver.moveNext();
        }
    }
}

export const SEARCH_LIBRARY_FOR_TUTORIAL_SCENE: TutorialStep = {
    driverStep: {
        element: '.searchbar', popover: {
            title: "Let's find a starter template", description: `<p>First, search for "Tutorial".</p>${toVideoElem("/img/tutorial/search-for-tutorial.webm")}`,
            side: 'top', align: "center",
            showButtons: ["close"],
        }
    },
    onSearchChanged(driver) {
        driver.moveNext();
    },
}

export const ASK_TO_DRAG_SCENE_TO_SOUNDBOARD: TutorialStep = {
    driverStep: {
        element: 'main',
        popover: {
            title: 'Now drag it to your soundboard', description: `${toVideoElem("/img/tutorial/drag-scene-to-board.webm")}`,
            side: "top", align: "center",
            onPopoverRender: () => {
                const elem = document.getElementById("soundboard-main");
                elem.focus();
            }
        }
    },
    onComponentMounted: (node: HTMLElement, driver: Driver) => {
        if (node.classList.contains("soundboard-lane")) {
            libraryOpen.set(false);
            driver.moveNext();
        }
    }
}

export const EXPLAIN_A_SCENE: TutorialStep = {
    driverStep: {
        element: ".soundboard-lane-header",
        popover: {
            title: "This is a <b>Scene</b>", description: "<b>Scene</b>s are just fancy containers for sound.",
            side: "top", align: "center",
        }
    }
}

export const EXPLAIN_SCENE_GLOBAL_CONTROLS: TutorialStep = {
    driverStep: {
        element: '.scene-global-controls',
        popover: {
            title: 'Give it a shot!', description: `<p>Press "Play Scene" to get the scene in motion.</p>${toVideoElem("/img/tutorial/click-play-scene.webm")}`,
            side: 'top', align: 'end',
        }
    },
}

export const EXPLAIN_EPIC_SCENE_SOUND: TutorialStep = {
    driverStep: {
        element: '.scene-global-controls',
        popover: {
            title: "🎉And that's it!", description: `<p>Prep is done!</p><p>For many games, prebuilt <b>Scenes</b> are all you need.</p>`,
            side: 'top', align: 'end',
        }
    },
}

export const STILL_HERE: TutorialStep = {
    driverStep: {
        element: '.scene-global-controls',
        popover: {
            title: "Still here?", description: `<p>Still here?</p><p>Great!</p><p>Let's explore how to get the best out of <b>Scenes</b>.</p>`,
            side: 'top', align: 'end',
        }
    },
}
export const ASK_TO_OPEN_SCENE: TutorialStep = {
    driverStep: {
        element: '.soundboard-lane-header',
        popover: {
            title: "Let's take a look inside", description: `<p>Click on the little arrow to open the <b>Scene</b>.</p>${toVideoElem("/img/tutorial/open-screen-demo.webm")}`,
            side: 'top', align: "end",
            showButtons: ["close"],
        }
    },
    onDetailsOpen(node, driver) {
        if (node.classList.contains("soundboard-lane")) {
            driver.moveNext();
        }
    },
}

export const EXPLAIN_AMBIENCE_LANE: TutorialStep = {
    driverStep: {
        element: '.ambience-lane',
        popover: {
            title: 'First, Ambiences', description: '<p>Howling wind. Rolling sea. A quiet river.</p><p>Ambiences are sounds that the characters can hear.</p><p>Use these to transport your players to your world.</p>',
            side: 'top', align: "start"
        }
    },
}

export const EXPLAIN_AMBIENCE_LANE_LOOPING: TutorialStep = {
    driverStep: {
        element: '.soundcard-enabled',
        popover: {
            title: 'First, Ambiences', description: '<p>When enabled, ambiences will loop non-stop</p>',
            side: 'top', align: "start"
        }
    },
}

export const EXPLAIN_AMBIENCE_ADVANCED: TutorialStep = {
    driverStep: {
        element: '.ambience-controls',
        popover: {
            title: 'First, Ambiences', description: `<p>You can control how they loop by clicking this wheel</p>${toVideoElem("/img/tutorial/advanced-ambiences-demo.webm")}`,
            side: 'top', align: "start"
        }
    },
}

export const EXPLAIN_MUSIC_LANE: TutorialStep = {
    driverStep: {
        element: '.music-lane',
        popover: {
            title: 'Now, Music', description: '<p>Music gives that emotional "Oomph".</p><p> Use it to make them tense, sad, or excited!</p>',
            side: 'top', align: "start"
        }
    },
}

export const EXPLAIN_SOUND_EFFECTS_LANE: TutorialStep = {
    driverStep: {
        element: '.sound-effects-lane',
        popover: {
            title: 'Last, Sound Effects', description: "<p>An unsettling piano. A sudden thunder. A dragon that roars in the distance.</p><p>Sound Effects are for surprises.</p>Use them sparingly for best effect!",
            side: 'top', align: "start"
        }
    },
}

export const NOW_LET_S_ADD_MUSIC: TutorialStep = {
    driverStep: {
        popover: {
            title: "Let's make this Epic!", description: "<p>I promised Epic sound, and for now we only have a farm.</p><p> Let's fix that!</p>",
            onNextClick: () => {
                libraryOpen.set(true)
            }
        }
    },
    onComponentMounted: (node: HTMLElement, driver: Driver) => {
        if (node.id == "library") {
            driver.moveNext();
        }
    }
}

export const INTERSTITIAL: TutorialStep = {
    driverStep: {
        element: '.library', popover: {
            title: "A fight breaks out!", description: "<p>Your PCs are brawling in the tavern again.</p><p>We need to find some Epic battle music, and quick!</p>",
            side: 'top', align: "center",
        }
    },
}
export const ASK_TO_CLICK_ON_MUSIC_FILTER: TutorialStep = {
    driverStep: {
        element: '#quick-filter-Music',
        popover: {
            title: "Let's find some epic Music", description: "<p>Let's find some epic Music</p><p>Click here.</p>",
            showButtons: ["close"],
        }
    },
    onComponentMounted: (node: HTMLElement, driver: Driver) => {
        if (node.classList.contains("sound")) {
            driver.moveNext();
        }
    }
}

export const SEARCH_LIBRARY_FOR_A_TAG: TutorialStep = {
    driverStep: {
        element: '.searchbar', popover: {
            title: "Let's search for some music!", description: `<p>Browse for some tags that could go with this scene:</p>${toVideoElem("/img/tutorial/search-for-tags.webm")}`,
            side: 'top', align: "center",
        }
    },
}
export const FIDDLE_WITH_MUSIC: TutorialStep = {
    driverStep: {
        element: '.library', popover: {
            title: "Explore!", description: `<p>Find an Epic track you like!</p><p>You can preview them by clicking the "Play" button.</p>${toVideoElem("/img/tutorial/preview-library-sound.webm")}`,
            side: 'top', align: "center",
        }
    },
}

export const ASK_TO_DRAG_MUSIC_TO_SCENE: TutorialStep = {
    driverStep: {
        element: '#soundboard-main',
        popover: {
            title: 'Now add the music to the scene', description: `<p>When you've found a track that you like, drag it to the scene!</p>${toVideoElem("/img/tutorial/drag-music.webm")}`,
            side: "top", align: "center",
            showButtons: ["close"],
            onPopoverRender: () => {
                const elem = document.getElementById("soundboard-main");
                elem.focus();
            }
        }
    },
    onComponentMounted: (node: HTMLElement, driver: Driver) => {
        if (node.id.startsWith("soundcard-")) {
            driver.moveNext();
        }
    }
}

export const NOW_PLAY_THE_SCENE: TutorialStep = {
    driverStep: {
        element: ".soundboard-lane",
        popover: {
            title: 'Press "Play Scene"', description: toVideoElem("/img/tutorial/click-play-scene.webm"),
            side: "top", align: "end",
        }
    }
}

export const THATS_IT: TutorialStep = {
    driverStep: {
        popover: {
            title: "That's it!", description: "<p>Welcome to Bardic Tools!</p><p>This tutorial barely scratched the surface of what you can do.</p><p>Keep exploring, and happy game!</p><p>Have more questions? <a href='/discord' target='_blank'>Get Support on Discord</a></p>",
            side: "top", align: "center",
        }
    },
}
